import React, { useState, useEffect, useRef } from 'react';
import './OrdersTable.css';

const OrdersTable = ({ orders, loading, onOrderClick, onStatusChange, currentPage, onPageChange, lastUpdated }) => {
  const [actionMenuOpen, setActionMenuOpen] = useState(null);
  const buttonRefs = useRef({});
  const dropdownRefs = useRef({});

  const formatTime = (date) => {
    return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (date) => {
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const getStatusBadge = (status) => {
    const badges = {
      New: { bg: '#FFF3CD', color: '#856404', icon: '🔔', text: 'New' },
      Preparing: { bg: '#feecec', color: '#fe0000', icon: '👨‍🍳', text: 'Preparing' },
      Ready: { bg: '#FFF4E6', color: '#F7B801', icon: '✅', text: 'Ready' },
      Delivered: { bg: '#D1F2EB', color: '#2D6A4F', icon: '✓', text: 'Delivered' },
      Cancelled: { bg: '#F8D7DA', color: '#D62828', icon: '✕', text: 'Cancelled' }
    };
    return badges[status] || badges.New;
  };

  const itemsPerPage = 10;
  const totalPages = Math.ceil(orders.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedOrders = orders.slice(startIndex, startIndex + itemsPerPage).map((order, index) => ({
    ...order,
    displayNumber: startIndex + index + 1 // Sequential number accounting for pagination
  }));

  const handleActionClick = (e, orderId) => {
    e.stopPropagation();
    setActionMenuOpen(actionMenuOpen === orderId ? null : orderId);
  };

  // Position dropdown when it opens
  useEffect(() => {
    if (actionMenuOpen) {
      const button = buttonRefs.current[actionMenuOpen];
      const dropdown = dropdownRefs.current[actionMenuOpen];
      
      if (button && dropdown) {
        const buttonRect = button.getBoundingClientRect();
        const dropdownHeight = dropdown.offsetHeight || 200; // Estimate if not rendered yet
        
        // Position above the button
        dropdown.style.top = `${buttonRect.top - dropdownHeight - 8}px`;
        dropdown.style.left = `${buttonRect.right - dropdown.offsetWidth}px`;
      }
    }
  }, [actionMenuOpen]);

  const handleActionSelect = (action, order) => {
    setActionMenuOpen(null);
    if (action === 'View Details') {
      onOrderClick(order);
    } else if (action === 'Cancel Order') {
      onStatusChange(order.id, 'Cancelled');
    }
  };

  if (loading) {
    return (
      <div className="orders-table-card">
        <div className="loading-skeleton">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="skeleton-row" />
          ))}
        </div>
      </div>
    );
  }

  if (orders.length === 0) {
    return (
      <div className="orders-table-card">
        <div className="empty-state">
          <span className="empty-icon">📦</span>
          <p className="empty-text">No orders found</p>
          <p className="empty-subtext">Orders will appear here once customers place them</p>
        </div>
      </div>
    );
  }

  const renderMobileCard = (order) => {
    const badge = getStatusBadge(order.status);
    return (
      <div key={order.id} className="mobile-order-card" onClick={() => onOrderClick(order)}>
        <div className="mobile-card-header">
          <span className="mobile-order-id">{order.displayNumber || order.orderNumber || order.id}</span>
          <span
            className="mobile-status-badge"
            style={{ backgroundColor: badge.bg, color: badge.color }}
          >
            {badge.icon} {badge.text}
          </span>
        </div>
        <div className="mobile-card-body">
          <div className="mobile-customer">
            <strong>{order.customer.name}</strong>
            <span>{order.customer.phone}</span>
          </div>
          <div className="mobile-items">
            {order.itemCount} items • {order.items.slice(0, 2).join(', ')}
            {order.items.length > 2 && '...'}
          </div>
          <div className="mobile-time">
            {formatTime(order.createdAt)} • {formatDate(order.createdAt)}
          </div>
        </div>
        <div className="mobile-card-footer">
          <div className="mobile-amount">PKR {order.amount.toLocaleString()}</div>
          <button
            className="mobile-action-btn"
            onClick={(e) => {
              e.stopPropagation();
              handleActionClick(e, order.id);
            }}
          >
            ⋮
          </button>
          {actionMenuOpen === order.id && (
            <div className="mobile-action-dropdown">
              <button onClick={() => handleActionSelect('View Details', order)}>
                View Details
              </button>
              <button onClick={() => handleActionSelect('Call Customer', order)}>
                Call Customer
              </button>
              <button onClick={() => handleActionSelect('Cancel Order', order)}>
                Cancel Order
              </button>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="orders-table-card">
      {lastUpdated && (
        <div className="last-updated">
          Updated {lastUpdated.toLocaleTimeString()}
        </div>
      )}
      
      {/* Desktop Table View */}
      <div className="table-wrapper">
        <table className="orders-table">
          <thead>
            <tr>
              <th style={{ width: '15%' }}>ORDER ID</th>
              <th style={{ width: '20%' }}>CUSTOMER</th>
              <th style={{ width: '15%' }}>ITEMS</th>
              <th style={{ width: '12%' }}>AMOUNT</th>
              <th style={{ width: '15%' }}>STATUS</th>
              <th style={{ width: '13%' }}>TIME</th>
              <th style={{ width: '10%' }}>ACTIONS</th>
            </tr>
          </thead>
          <tbody>
            {paginatedOrders.map((order) => {
              const badge = getStatusBadge(order.status);
              return (
                <tr key={order.id} onClick={() => onOrderClick(order)}>
                  <td>
                    <span className="order-id-link">{order.displayNumber || order.orderNumber || order.id}</span>
                  </td>
                  <td>
                    <div className="customer-info">
                      <div className="customer-name">{order.customer.name}</div>
                      <div className="customer-phone">{order.customer.phone}</div>
                    </div>
                  </td>
                  <td>
                    <div className="items-info">
                      <div className="items-count">{order.itemCount} items</div>
                      <div className="items-names">
                        {order.items.slice(0, 2).join(', ')}
                        {order.items.length > 2 && '...'}
                      </div>
                    </div>
                  </td>
                  <td>
                    <div className="amount">PKR {order.amount.toLocaleString()}</div>
                  </td>
                  <td>
                    <span
                      className="status-badge"
                      style={{ backgroundColor: badge.bg, color: badge.color }}
                    >
                      {badge.icon} {badge.text}
                    </span>
                  </td>
                  <td>
                    <div className="time-info">
                      <div className="time">{formatTime(order.createdAt)}</div>
                      <div className="date">{formatDate(order.createdAt)}</div>
                    </div>
                  </td>
                  <td>
                    <div className="actions-cell" onClick={(e) => e.stopPropagation()}>
                      <button
                        className="action-menu-btn"
                        onClick={(e) => handleActionClick(e, order.id)}
                        ref={(el) => {
                          if (el) {
                            buttonRefs.current[order.id] = el;
                          }
                        }}
                      >
                        ⋮
                      </button>
                      {actionMenuOpen === order.id && (
                        <div 
                          className="action-dropdown"
                          ref={(el) => {
                            if (el) {
                              dropdownRefs.current[order.id] = el;
                            }
                          }}
                        >
                          <button onClick={() => handleActionSelect('View Details', order)}>
                            View Details
                          </button>
                          <button onClick={() => handleActionSelect('Print Order', order)}>
                            Print Order
                          </button>
                          <button onClick={() => handleActionSelect('Call Customer', order)}>
                            Call Customer
                          </button>
                          <button onClick={() => handleActionSelect('Cancel Order', order)}>
                            Cancel Order
                          </button>
                        </div>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Mobile Card View */}
      <div className="mobile-orders-list">
        {paginatedOrders.map(order => renderMobileCard(order))}
      </div>

      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        totalItems={orders.length}
        itemsPerPage={itemsPerPage}
        onPageChange={onPageChange}
      />
    </div>
  );
};

const Pagination = ({ currentPage, totalPages, totalItems, itemsPerPage, onPageChange }) => {
  const startItem = (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, totalItems);

  const getPageNumbers = () => {
    const pages = [];
    const maxVisible = 5;
    
    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 3) {
        for (let i = 1; i <= 5; i++) {
          pages.push(i);
        }
        pages.push('...');
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push(1);
        pages.push('...');
        for (let i = totalPages - 4; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        pages.push(1);
        pages.push('...');
        for (let i = currentPage - 1; i <= currentPage + 1; i++) {
          pages.push(i);
        }
        pages.push('...');
        pages.push(totalPages);
      }
    }
    
    return pages;
  };

  return (
    <div className="pagination">
      <div className="pagination-info">
        Showing {startItem}-{endItem} of {totalItems} orders
      </div>
      <div className="pagination-controls">
        <button
          className="pagination-btn"
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
        >
          &lt; Previous
        </button>
        {getPageNumbers().map((page, index) => (
          <button
            key={index}
            className={`pagination-btn ${page === currentPage ? 'active' : ''} ${page === '...' ? 'ellipsis' : ''}`}
            onClick={() => typeof page === 'number' && onPageChange(page)}
            disabled={page === '...'}
          >
            {page}
          </button>
        ))}
        <button
          className="pagination-btn"
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
        >
          Next &gt;
        </button>
      </div>
    </div>
  );
};

export default OrdersTable;

